// src/pages/IntelligenceDashboardHome.jsx

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faBrain,
  faChartLine,
  faProjectDiagram,
  faUserCheck,
  faBullseye,
  faLightbulb,
  faExclamationTriangle,
  faRobot,
} from "@fortawesome/free-solid-svg-icons";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import { Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const IntelligenceDashboardHome = () => {
  const evolutionData = {
    labels: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"],
    datasets: [
      {
        label: "Thinking Maturity",
        data: [22, 38, 49, 68, 82],
        fill: true,
        borderColor: "#22d3ee",
        backgroundColor: "rgba(34,211,238,0.15)",
        tension: 0.4,
      },
    ],
  };

  const decisionData = {
    labels: ["Accurate", "Needs Review", "High Risk"],
    datasets: [
      {
        data: [62, 24, 14],
        backgroundColor: ["#06b6d4", "#f59e0b", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  const kpis = [
    {
      title: "Thinking Maturity",
      value: "82%",
      icon: <FontAwesomeIcon icon={faBrain} />,
    },
    {
      title: "Decision Quality",
      value: "74%",
      icon: <FontAwesomeIcon icon={faBullseye} />,
    },
    {
      title: "Learning Conversion",
      value: "68%",
      icon: <FontAwesomeIcon icon={faProjectDiagram} />,
    },
    {
      title: "Behavior Growth",
      value: "+31%",
      icon: <FontAwesomeIcon icon={faChartLine} />,
    },
  ];

  return (
    <div className="min-h-screen bg-black text-cyan-100 px-3 py-4">

      {/* HEADER */}
      <div className="mb-4">
        <div className="text-xl font-bold text-cyan-300 tracking-wide">
          📊 Intelligence Dashboard
        </div>

        <div className="text-xs text-cyan-500 mt-1">
          Orgstead Learning Intelligence System
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {kpis.map((item, idx) => (
          <div
            key={idx}
            className="bg-cyan-950/40 border border-cyan-900 rounded-xl p-3 shadow-md"
          >
            <div className="flex items-center justify-between">
              <div className="text-cyan-300 text-lg">
                {item.icon}
              </div>

              <div className="text-lg font-bold text-yellow-200">
                {item.value}
              </div>
            </div>

            <div className="mt-2 text-[11px] text-cyan-100">
              {item.title}
            </div>
          </div>
        ))}
      </div>

      {/* THINKING EVOLUTION */}
      <div className="bg-cyan-950/30 border border-cyan-900 rounded-2xl p-3 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faChartLine} className="text-cyan-300" />
          <div className="text-sm font-semibold text-cyan-200">
            Thinking Evolution
          </div>
        </div>

        <Line
          data={evolutionData}
          options={{
            responsive: true,
            plugins: {
              legend: {
                labels: {
                  color: "#cbd5e1",
                },
              },
            },
            scales: {
              x: {
                ticks: {
                  color: "#94a3b8",
                },
                grid: {
                  color: "rgba(255,255,255,0.05)",
                },
              },
              y: {
                ticks: {
                  color: "#94a3b8",
                },
                grid: {
                  color: "rgba(255,255,255,0.05)",
                },
              },
            },
          }}
        />
      </div>

      {/* DECISION QUALITY */}
      <div className="bg-cyan-950/30 border border-cyan-900 rounded-2xl p-3 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <FontAwesomeIcon icon={faBullseye} className="text-yellow-300" />
          <div className="text-sm font-semibold text-cyan-200">
            Decision Intelligence
          </div>
        </div>

        <div className="max-w-[240px] mx-auto">
          <Doughnut
            data={decisionData}
            options={{
              plugins: {
                legend: {
                  labels: {
                    color: "#cbd5e1",
                  },
                },
              },
            }}
          />
        </div>
      </div>

      {/* AI INSIGHTS */}
      <div className="bg-cyan-950/30 border border-cyan-900 rounded-2xl p-3 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faRobot} className="text-cyan-300" />
          <div className="text-sm font-semibold text-cyan-200">
            AI Insight Layer
          </div>
        </div>

        <div className="space-y-2">

          <div className="bg-black/40 border border-cyan-900 rounded-xl p-3 text-xs">
            Teams show strong strategic thinking but weak execution consistency.
          </div>

          <div className="bg-black/40 border border-cyan-900 rounded-xl p-3 text-xs">
            Cognitive fatigue spikes after 42 mins in ambiguity simulations.
          </div>

          <div className="bg-black/40 border border-cyan-900 rounded-xl p-3 text-xs">
            Leadership group has high confidence but medium reasoning depth.
          </div>

        </div>
      </div>

      {/* COGNITIVE FLAGS */}
      <div className="bg-cyan-950/30 border border-cyan-900 rounded-2xl p-3 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-300" />
          <div className="text-sm font-semibold text-cyan-200">
            Cognitive Risk Flags
          </div>
        </div>

        <div className="space-y-2 text-xs">

          <div className="flex items-start gap-2">
            <span>⚠️</span>
            <span>Anchoring bias detected in pricing simulations</span>
          </div>

          <div className="flex items-start gap-2">
            <span>⚠️</span>
            <span>Decision reversals increasing in mid-management layer</span>
          </div>

          <div className="flex items-start gap-2">
            <span>⚠️</span>
            <span>Low reflection depth in communication workshops</span>
          </div>

        </div>
      </div>

      {/* ORGANIZATIONAL MAP */}
      <div className="bg-cyan-950/30 border border-cyan-900 rounded-2xl p-3">
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faUserCheck} className="text-cyan-300" />
          <div className="text-sm font-semibold text-cyan-200">
            Organizational Intelligence Snapshot
          </div>
        </div>

        <div className="space-y-3 text-xs">

          <div className="flex justify-between border-b border-cyan-900 pb-2">
            <span>Strategy Team</span>
            <span className="text-yellow-200">
              High Thinking / Medium Execution
            </span>
          </div>

          <div className="flex justify-between border-b border-cyan-900 pb-2">
            <span>Sales Team</span>
            <span className="text-yellow-200">
              Fast Decisions / Low Consistency
            </span>
          </div>

          <div className="flex justify-between">
            <span>Leadership Pool</span>
            <span className="text-yellow-200">
              Strong Cognitive Maturity
            </span>
          </div>

        </div>
      </div>

    </div>
  );
};

export default IntelligenceDashboardHome;